export const PRESCRIPTION_FORM_INITIAL = {
  PDOneorTwoNumbers: null,
  SingularPDValue: null,
  ODRightSphere_SPH: null,
  ODRightCylinder_CYL: null,
  ODRightAxis: null,
  ODRightADD: null,
  ODRightPupillaryDistance_PD: null,
  OSLeftSphere_SPH: null,
  OSLeftCylinder_CYL: null,
  OSLeftAxis: null,
  OSLeftADD: null,
  OSLeftPupillaryDistance_PD: null,
};

/** Unwrap dropdown option objects `{ label, value }` → primitive. */
export function unwrapOptionValue(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    if ("value" in value) return unwrapOptionValue(value.value);
    if ("label" in value) return unwrapOptionValue(value.label);
    return null;
  }
  return value;
}

function toFieldValue(value) {
  const unwrapped = unwrapOptionValue(value);
  if (unwrapped === null || unwrapped === undefined || unwrapped === "") {
    return undefined;
  }
  return String(unwrapped);
}

function toFormNumber(value) {
  const unwrapped = unwrapOptionValue(value);
  if (unwrapped === null || unwrapped === undefined || unwrapped === "") {
    return null;
  }
  const n = Number(unwrapped);
  return Number.isNaN(n) ? null : n;
}

/** Map order-details / PrescriptionList form values → VA confirm shape. */
export function prescriptionFormToVa(formValues, pdOption, uploadedFile) {
  const pdType = pdOption || formValues?.PDOneorTwoNumbers || null;
  const normalized = {
    ODRightSphere_SPH: toFormNumber(formValues?.ODRightSphere_SPH),
    ODRightCylinder_CYL: toFormNumber(formValues?.ODRightCylinder_CYL),
    ODRightAxis: toFormNumber(formValues?.ODRightAxis),
    ODRightADD: toFormNumber(formValues?.ODRightADD),
    ODRightPupillaryDistance_PD: toFormNumber(
      formValues?.ODRightPupillaryDistance_PD
    ),
    OSLeftSphere_SPH: toFormNumber(formValues?.OSLeftSphere_SPH),
    OSLeftCylinder_CYL: toFormNumber(formValues?.OSLeftCylinder_CYL),
    OSLeftAxis: toFormNumber(formValues?.OSLeftAxis),
    OSLeftADD: toFormNumber(formValues?.OSLeftADD),
    OSLeftPupillaryDistance_PD: toFormNumber(
      formValues?.OSLeftPupillaryDistance_PD
    ),
    SingularPDValue: toFormNumber(formValues?.SingularPDValue),
    PDOneorTwoNumbers: pdType,
    prescriptionType: formValues?.prescriptionType || "Progressive Lenses",
  };

  return {
    od: {
      sph: toFieldValue(normalized.ODRightSphere_SPH),
      cyl: toFieldValue(normalized.ODRightCylinder_CYL),
      axis: toFieldValue(normalized.ODRightAxis),
      add: toFieldValue(normalized.ODRightADD),
      pd: toFieldValue(normalized.ODRightPupillaryDistance_PD),
    },
    os: {
      sph: toFieldValue(normalized.OSLeftSphere_SPH),
      cyl: toFieldValue(normalized.OSLeftCylinder_CYL),
      axis: toFieldValue(normalized.OSLeftAxis),
      add: toFieldValue(normalized.OSLeftADD),
      pd: toFieldValue(normalized.OSLeftPupillaryDistance_PD),
    },
    pdType,
    singularPd: normalized.SingularPDValue,
    fileUrl: uploadedFile ? URL.createObjectURL(uploadedFile) : null,
    fileName: uploadedFile?.name || null,
    formValues: {
      ...PRESCRIPTION_FORM_INITIAL,
      ...normalized,
    },
  };
}

/** Hydrate PrescriptionList form values from VA / parse prescription shape. */
export function vaPrescriptionToFormValues(prescription = {}) {
  if (prescription?.formValues) {
    return {
      ...PRESCRIPTION_FORM_INITIAL,
      ODRightSphere_SPH: toFormNumber(prescription.formValues.ODRightSphere_SPH),
      ODRightCylinder_CYL: toFormNumber(
        prescription.formValues.ODRightCylinder_CYL
      ),
      ODRightAxis: toFormNumber(prescription.formValues.ODRightAxis),
      ODRightADD: toFormNumber(prescription.formValues.ODRightADD),
      ODRightPupillaryDistance_PD: toFormNumber(
        prescription.formValues.ODRightPupillaryDistance_PD
      ),
      OSLeftSphere_SPH: toFormNumber(prescription.formValues.OSLeftSphere_SPH),
      OSLeftCylinder_CYL: toFormNumber(
        prescription.formValues.OSLeftCylinder_CYL
      ),
      OSLeftAxis: toFormNumber(prescription.formValues.OSLeftAxis),
      OSLeftADD: toFormNumber(prescription.formValues.OSLeftADD),
      OSLeftPupillaryDistance_PD: toFormNumber(
        prescription.formValues.OSLeftPupillaryDistance_PD
      ),
      SingularPDValue: toFormNumber(prescription.formValues.SingularPDValue),
      PDOneorTwoNumbers:
        prescription.pdType ||
        prescription.formValues.PDOneorTwoNumbers ||
        null,
      prescriptionType:
        prescription.formValues.prescriptionType || "Progressive Lenses",
    };
  }

  const od = prescription?.od || {};
  const os = prescription?.os || {};
  return {
    ...PRESCRIPTION_FORM_INITIAL,
    PDOneorTwoNumbers: prescription?.pdType || null,
    SingularPDValue: toFormNumber(
      prescription?.singularPd ?? prescription?.pdDistance
    ),
    ODRightSphere_SPH: toFormNumber(
      od.sph ?? od.sphere ?? prescription?.ODRightSphere_SPH
    ),
    ODRightCylinder_CYL: toFormNumber(
      od.cyl ?? od.cylinder ?? prescription?.ODRightCylinder_CYL
    ),
    ODRightAxis: toFormNumber(od.axis ?? prescription?.ODRightAxis),
    ODRightADD: toFormNumber(od.add ?? prescription?.ODRightADD),
    ODRightPupillaryDistance_PD: toFormNumber(
      od.pd ?? prescription?.ODRightPupillaryDistance_PD
    ),
    OSLeftSphere_SPH: toFormNumber(
      os.sph ?? os.sphere ?? prescription?.OSLeftSphere_SPH
    ),
    OSLeftCylinder_CYL: toFormNumber(
      os.cyl ?? os.cylinder ?? prescription?.OSLeftCylinder_CYL
    ),
    OSLeftAxis: toFormNumber(os.axis ?? prescription?.OSLeftAxis),
    OSLeftADD: toFormNumber(os.add ?? prescription?.OSLeftADD),
    OSLeftPupillaryDistance_PD: toFormNumber(
      os.pd ?? prescription?.OSLeftPupillaryDistance_PD
    ),
    prescriptionType: "Progressive Lenses",
  };
}

/**
 * Legacy savePrescription-shaped payload (flat numeric fields).
 * Matches POST /prescriptions/savePrescription field names.
 */
export function serializeLegacyPrescription(prescription = {}) {
  const form =
    prescription?.formValues ||
    vaPrescriptionToFormValues(prescription);
  const pdType =
    form.PDOneorTwoNumbers || prescription?.pdType || null;

  const payload = {
    ODRightSphere_SPH: toFormNumber(form.ODRightSphere_SPH),
    ODRightCylinder_CYL: toFormNumber(form.ODRightCylinder_CYL),
    ODRightAxis: toFormNumber(form.ODRightAxis),
    ODRightADD: toFormNumber(form.ODRightADD),
    OSLeftSphere_SPH: toFormNumber(form.OSLeftSphere_SPH),
    OSLeftCylinder_CYL: toFormNumber(form.OSLeftCylinder_CYL),
    OSLeftAxis: toFormNumber(form.OSLeftAxis),
    OSLeftADD: toFormNumber(form.OSLeftADD),
    PDOneorTwoNumbers: pdType,
    prescriptionType: form.prescriptionType || "Progressive Lenses",
  };

  if (pdType === "One") {
    payload.SingularPDValue = toFormNumber(
      form.SingularPDValue ?? prescription?.singularPd
    );
    payload.ODRightPupillaryDistance_PD = null;
    payload.OSLeftPupillaryDistance_PD = null;
  } else if (pdType === "Two") {
    payload.SingularPDValue = null;
    payload.ODRightPupillaryDistance_PD = toFormNumber(
      form.ODRightPupillaryDistance_PD
    );
    payload.OSLeftPupillaryDistance_PD = toFormNumber(
      form.OSLeftPupillaryDistance_PD
    );
  } else {
    payload.SingularPDValue = toFormNumber(
      form.SingularPDValue ?? prescription?.singularPd
    );
    payload.ODRightPupillaryDistance_PD = toFormNumber(
      form.ODRightPupillaryDistance_PD
    );
    payload.OSLeftPupillaryDistance_PD = toFormNumber(
      form.OSLeftPupillaryDistance_PD
    );
  }

  if (prescription?.fileName) {
    payload.fileName = prescription.fileName;
  }

  return payload;
}

/** Numeric prescription fields on order update (must be numbers or omitted). */
export const ORDER_RX_NUMERIC_FIELD_KEYS = [
  "ODRightSphere_SPH",
  "ODRightCylinder_CYL",
  "ODRightAxis",
  "ODRightADD",
  "ODRightPupillaryDistance_PD",
  "OSLeftSphere_SPH",
  "OSLeftCylinder_CYL",
  "OSLeftAxis",
  "OSLeftADD",
  "OSLeftPupillaryDistance_PD",
  "SingularPDValue",
];

/**
 * Normalize a changed order field for PATCH payload.
 * Empty prescription numerics are omitted — backend rejects "" / null as non-numbers.
 */
export function orderUpdatePayloadValue(field, value) {
  if (field === "Status") {
    return Number(value);
  }
  if (field === "PDOneorTwoNumbers") {
    if (value === "" || value == null) return undefined;
    return value;
  }
  if (ORDER_RX_NUMERIC_FIELD_KEYS.includes(field)) {
    if (value === "" || value == null) return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return value;
}

/** File picker filter: PDF and still images only (no GIF). */
export const PRESCRIPTION_UPLOAD_ACCEPT =
  "application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp";

const PRESCRIPTION_UPLOAD_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]);

const PRESCRIPTION_UPLOAD_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".webp"];

export function isMultiLensPrescription(prescription = {}) {
  return (
    Array.isArray(prescription?.prescriptions) &&
    prescription.prescriptions.length > 1
  );
}

export function normalizeLensItems(lensItems = []) {
  return (Array.isArray(lensItems) ? lensItems : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

export function getPrescriptionLensCount(lensItems = []) {
  const items = normalizeLensItems(lensItems);
  return items.length > 1 ? items.length : 1;
}

export function getPrescriptionEntry(
  prescription = {},
  lensIndex = 0,
  isMultiLens = false
) {
  if (prescription?.skipped) return null;
  if (isMultiLens) {
    return prescription?.prescriptions?.[lensIndex] || null;
  }
  return lensIndex === 0 ? prescription : null;
}

export function prescriptionHasEnteredValues(
  formValues,
  selectedOption,
  hasUploadedFile = false
) {
  if (hasUploadedFile) return true;

  return [
    formValues?.ODRightSphere_SPH,
    formValues?.ODRightCylinder_CYL,
    formValues?.ODRightAxis,
    formValues?.ODRightADD,
    formValues?.ODRightPupillaryDistance_PD,
    formValues?.OSLeftSphere_SPH,
    formValues?.OSLeftCylinder_CYL,
    formValues?.OSLeftAxis,
    formValues?.OSLeftADD,
    formValues?.OSLeftPupillaryDistance_PD,
    formValues?.SingularPDValue,
    selectedOption,
  ].some((value) => value !== null && value !== undefined && value !== "");
}

export function buildMultiLensPrescriptionDraft({
  lensItems = [],
  formValuesMap = {},
  selectedOptionMap = {},
  uploadedFileMap = {},
  initialPrescription = {},
}) {
  const labels = normalizeLensItems(lensItems);
  const prescriptions = labels.map((label, index) => {
    const uploadedFile = uploadedFileMap[index] || null;
    const initialEntry = initialPrescription?.prescriptions?.[index];
    const hasUploadedFile = Boolean(
      uploadedFile || (!initialPrescription?.skipped && initialEntry?.fileName)
    );
    const hasData = prescriptionHasEnteredValues(
      formValuesMap[index] || PRESCRIPTION_FORM_INITIAL,
      selectedOptionMap[index] || null,
      hasUploadedFile
    );

    if (!hasData) {
      return {
        label,
        skipped: true,
      };
    }

    const prescription = prescriptionFormToVa(
      formValuesMap[index] || PRESCRIPTION_FORM_INITIAL,
      selectedOptionMap[index] || null,
      uploadedFile
    );

    if (!uploadedFile && initialEntry?.fileName) {
      prescription.fileName = initialEntry.fileName;
      prescription.fileUrl = initialEntry.fileUrl ?? null;
    }

    return {
      ...prescription,
      label,
      skipped: false,
    };
  });

  return {
    skipped: false,
    lensCount: labels.length,
    prescriptions,
  };
}

export function isMultiLensSelection(selection = {}) {
  return (
    Array.isArray(selection?.selections) && selection.selections.length > 1
  );
}

export function getLensSelectionEntry(selection = {}, lensIndex = 0) {
  if (isMultiLensSelection(selection)) {
    return selection.selections[lensIndex] || null;
  }
  return lensIndex === 0 ? selection : null;
}

export function buildLensSelectionDraft({
  lensItems = [],
  selections = [],
}) {
  const labels = normalizeLensItems(lensItems);
  if (labels.length <= 1) {
    return {
      ...(selections[0] || {}),
    };
  }

  return {
    lensCount: labels.length,
    selections: labels.map((label, index) => ({
      label,
      ...(selections[index] || {}),
    })),
  };
}

export function getPrescriptionUploadError(file) {
  if (!file) return "Select a file.";
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  if (name.endsWith(".gif") || type === "image/gif") {
    return "GIF files are not allowed. Upload a PDF, PNG, or JPEG.";
  }
  const extOk = PRESCRIPTION_UPLOAD_EXTS.some((ext) => name.endsWith(ext));
  const typeOk = PRESCRIPTION_UPLOAD_TYPES.has(type);
  if (!extOk && !typeOk) {
    return "Only PDF, PNG, and JPEG files are allowed.";
  }
  return "";
}
